/**
 * `ColumnDef` list for the unified inventory table (equipment + component), in
 * legacy order: Type badge · Name link · Service Tag / Ref · Category · Status
 * pill · Condition chip · Location · Supplier · Owner · Created. Equipment links
 * to /equipment/:id, component to /components/:id. Built on the shared item-type
 * Chip + inventory status/condition renderers.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import type { Item } from '@/api/capacities';
import { TableLink } from '@/components/table-link';
import { itemTypeCell } from '../../shared/masters-status';
import { itemStatusCell, itemConditionCell } from '../items-status';
import { formatDate } from '@/lib/format';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

/** Detail route for a row — equipment and component live under separate paths. */
export const itemDetailPath = (item: Item): string =>
  item.item_type === 'component'
    ? `/capacities/components/${item.id}`
    : `/capacities/equipment/${item.id}`;

const dash = <span style={muted}>—</span>;

export const itemColumns: ColumnDef<Item, unknown>[] = [
  {
    id: 'item_type',
    header: 'Type',
    cell: ({ row }) => itemTypeCell(row.original.item_type),
  },
  {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <TableLink to={itemDetailPath(row.original)} title={row.original.name} truncate>
        {row.original.name || row.original.id}
      </TableLink>
    ),
  },
  {
    id: 'reference',
    header: 'Service Tag / Ref',
    cell: ({ row }) => {
      const ref = row.original.service_tag || row.original.ref;
      return ref ? <span title={ref}>{ref}</span> : dash;
    },
  },
  {
    id: 'category',
    header: 'Category',
    cell: ({ row }) =>
      row.original.category ? (
        <span style={{ textTransform: 'capitalize' }}>{row.original.category}</span>
      ) : (
        dash
      ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => itemStatusCell(row.original.status),
  },
  {
    id: 'condition',
    header: 'Condition',
    cell: ({ row }) => itemConditionCell(row.original.condition),
  },
  {
    id: 'location',
    header: 'Location',
    cell: ({ row }) =>
      row.original.current_location?.name ? (
        <span title={row.original.current_location.name}>{row.original.current_location.name}</span>
      ) : (
        dash
      ),
  },
  {
    id: 'supplier',
    header: 'Supplier',
    cell: ({ row }) =>
      row.original.supplier?.name ? (
        <span title={row.original.supplier.name}>{row.original.supplier.name}</span>
      ) : (
        dash
      ),
  },
  {
    id: 'owner',
    header: 'Owner',
    cell: ({ row }) =>
      row.original.owner ? (
        <span title={row.original.owner}>{row.original.owner}</span>
      ) : (
        dash
      ),
  },
  {
    id: 'created_at',
    header: 'Created',
    cell: ({ row }) =>
      row.original.created_at ? formatDate(row.original.created_at) : dash,
  },
];
